import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useCartSync } from "@/hooks/useCartSync";
import { useLanguage } from "@/lib/i18n";
import { GlobalSurvey } from "@/components/GlobalSurvey";
import { ScrollToTop } from "@/components/ScrollToTop";
import { FacebookPixel } from "@/components/FacebookPixel";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { BackToTop } from "@/components/BackToTop";
import Index from "./pages/Index";
import ProductPage from "./pages/ProductPage";
import TermsPage from "./pages/TermsPage";
import ReturnPolicyPage from "./pages/ReturnPolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import NotFound from "./pages/NotFound";

import BlogIndex from "./pages/BlogIndex";
import BlogArticle from "./pages/BlogArticle";
import Dashboard from "./pages/Dashboard";

const queryClient = new QueryClient();

// Wrapper component to use hooks
function AppContent() {
  useCartSync();
  const initializeFromLocation = useLanguage(state => state.initializeFromLocation);
  const language = useLanguage(state => state.language);
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  
  useEffect(() => {
    initializeFromLocation();
  }, [initializeFromLocation]);

  // Dynamic <html lang> and dir for SEO — Google uses this as a strong indexing signal
  useEffect(() => {
    const langMap: Record<string, string> = {
      'en-US': 'en-US', 'en-GB': 'en-GB', 'en-AU': 'en-AU',
      'he': 'he', 'hr': 'hr', 'fi': 'fi', 'fr': 'fr',
      'de': 'de', 'no': 'nb', 'es': 'es', 'ar': 'ar',
    };
    document.documentElement.lang = langMap[language] || 'en';
    document.documentElement.dir = (language === 'he' || language === 'ar') ? 'rtl' : 'ltr';
  }, [language]);
  
  return (
    <>
      <ScrollToTop />
      {!isDashboard && <FacebookPixel />}
      {!isDashboard && <GoogleAnalytics />}
      <div className="page-transition-enter" key={undefined}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/product/:handle" element={<ProductPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/returns" element={<ReturnPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/shipping" element={<ShippingPolicyPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          
          <Route path="/dashboard" element={<Dashboard />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {!isDashboard && <GlobalSurvey />}
      {!isDashboard && <BackToTop />}
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;